import React from 'react';
import './LegalPages.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <div className="legal-content">
          <h1>Privacy Policy</h1>
          <p className="legal-updated">Last updated: January 20, 2026</p>

          <section>
            <h2>1. Introduction</h2>
            <p>
              Welcome to PrivateSold ("we," "our," or "us"). We respect your privacy and are 
              committed to protecting your personal data. This Privacy Policy explains how we 
              collect, use, disclose, and safeguard your information when you use our website 
              and services at privatesold.com.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, phone number, and password when you create an account</li>
              <li><strong>Transaction Details:</strong> Information you enter when creating bills of sale or invoices, including buyer/seller names, addresses, item descriptions, and prices</li>
              <li><strong>Payment Information:</strong> When you subscribe to our premium service, payment details are processed securely by Stripe. We do not store your full credit card number.</li>
              <li><strong>Signatures:</strong> Electronic signatures you or buyers provide for document signing</li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <ul>
              <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent on pages, clicks, and navigation patterns</li>
              <li><strong>Cookies:</strong> We use cookies to maintain your session, remember preferences, and analyze usage</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve our services</li>
              <li>Generate and store your bills of sale and invoices</li>
              <li>Process your transactions and subscription payments</li>
              <li>Send you service-related communications and updates</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Detect, prevent, and address technical issues or fraud</li>
              <li>Display relevant advertisements (for free tier users)</li>
              <li>Analyze usage patterns to improve user experience</li>
            </ul>
          </section>

          <section>
            <h2>4. Information Sharing</h2>
            <p>We may share your information with:</p>
            <ul>
              <li><strong>Service Providers:</strong> Third-party companies that help us operate our service (e.g., Stripe for payments, cloud hosting providers)</li>
              <li><strong>Advertising Partners:</strong> Google AdSense may display ads to free tier users and collect data according to their privacy policy</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p>We do not sell your personal information to third parties.</p>
          </section>

          <section>
            <h2>5. Data Retention</h2>
            <p>
              <strong>Free Users:</strong> Documents are generated for immediate download but are not stored on our servers.
            </p>
            <p>
              <strong>Premium Users:</strong> Your documents are stored securely for the duration of your subscription plus 30 days after cancellation.
            </p>
            <p>
              Account information is retained until you delete your account or request deletion.
            </p>
          </section>

          <section>
            <h2>6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect 
              your personal data, including encryption in transit (HTTPS) and at rest, secure 
              authentication, and regular security assessments. However, no method of transmission 
              over the Internet is 100% secure.
            </p>
          </section>

          <section>
            <h2>7. Your Rights</h2>
            <p>Depending on your location, you may have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict certain processing</li>
              <li>Data portability (receive your data in a structured format)</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>
            <p>To exercise these rights, please contact us at privacy@privatesold.com.</p>
          </section>

          <section>
            <h2>8. Cookies</h2>
            <p>We use the following types of cookies:</p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for the website to function (authentication, security)</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our site</li>
              <li><strong>Advertising Cookies:</strong> Used by Google AdSense to display relevant ads (free tier only)</li>
            </ul>
            <p>You can control cookies through your browser settings, but disabling essential cookies may affect functionality.</p>
          </section>

          <section>
            <h2>9. Third-Party Services</h2>
            <p>Our service integrates with:</p>
            <ul>
              <li><strong>Stripe:</strong> For payment processing. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a></li>
              <li><strong>Google AdSense:</strong> For advertising. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a></li>
              <li><strong>MongoDB Atlas:</strong> For data storage. See <a href="https://www.mongodb.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">MongoDB's Privacy Policy</a></li>
            </ul>
          </section>

          <section>
            <h2>10. Children's Privacy</h2>
            <p>
              Our service is not intended for individuals under 18 years of age. We do not 
              knowingly collect personal information from children. If you believe we have 
              collected information from a minor, please contact us immediately.
            </p>
          </section>

          <section>
            <h2>11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your 
              own. We ensure appropriate safeguards are in place to protect your data in 
              accordance with this Privacy Policy.
            </p>
          </section>

          <section>
            <h2>12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of 
              significant changes by posting a notice on our website or sending you an email. 
              Your continued use of the service after changes constitutes acceptance of the 
              updated policy.
            </p>
          </section>

          <section>
            <h2>13. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us at:</p>
            <ul className="contact-list">
              <li>Email: privacy@privatesold.com</li>
              <li>Website: privatesold.com</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
